/**
 * `mockups/client/files.html` §1 — the month, which is the page's answer.
 *
 * The hero is the count (`month.files`), the rows are the month's own files and
 * everything older is one `Fold` with a table in it — DESIGN.md P3, "show the
 * exception, fold the routine". The world delivers **41 individual files** in
 * August where the mockup draws seven grouped rows ("Reels — 14 edits, 9:16"),
 * so the seven most recent are on the card and the other thirty-four are behind
 * the disclosure; the grouping the mockup prints is not a field
 * (`grouped_files` is `0`) and this page does not invent one.
 *
 * A row that is **held** says which kind of held it is (`fileType.heldLabel`) in
 * place of its download link, and a row whose review has been requested carries
 * the page's only `Pill need`: ochre means "this needs your decision" (P4), and a
 * deliverable waiting on the client is exactly that.
 *
 * **The pill is the state and the two buttons beside it are the answer.**
 * `POST /api/clients/me/files/:id/review` has existed since session 16 and the
 * `file_review_requested` mail asks a client to use it; until this session nothing
 * on the page called it, so the pill and the "N are waiting on your review"
 * sentence were both terminal. *Request changes* opens `DecisionCard`'s own
 * `NoteDialog`, because refusing a deliverable and declining a quote are the same
 * interaction and the studio needs the sentence either way — and *Approve* now
 * opens a dialog of its own (R3-15): it wrote on the first click, and it is a
 * sign-off nothing on this page can take back. The dialog beside `NoteDialog`
 * below is where that argument is written out. Neither button is ochre: the row
 * already carries the page's one ochre thing and a second would be a louder row
 * saying the same thing (DESIGN.md P4, P7).
 *
 * **Who may answer.** `files.recordReview` asserts `requests:write`, so a `billing`
 * seat sees the pill and no buttons — `.docs/19` §Edge cases asks for absent, not
 * disabled.
 *
 * **Impersonating staff may ask for changes and may not approve** (session 24, S-1).
 * Approving is the client's sign-off on the work: it writes `reviewed_by` and
 * `review_outcome` on the deliverable, so an impersonated approval leaves the
 * authority for accepting it saying the client accepted it. That is the rule the
 * portal already applies everywhere else — "an impersonated session may not take a
 * decision that binds the client" — and this control was the one binding decision
 * never wired to it, on the client and on the server. `files.recordReview` refuses
 * it now through `security/impersonation.ts`, and this is the SPA reading the same
 * rule so that no control is offered to somebody the API will refuse.
 *
 * **Disabled with the reason, not absent** — which is the opposite of what a
 * `billing` seat gets, and deliberately: `components/client/DecisionCard.tsx` says
 * why at length. A `member` may never approve, so a button would be a promise the
 * account will not keep; a studio member viewing as somebody is a reader who can be
 * told *why*, and there is something they can do about it. This matches what the
 * sibling controls on `/app/requests` do exactly — a visible `Notice`, the button
 * `disabled` and `aria-disabled` pointing at it with `aria-describedby`, and the
 * one control that commits nothing (there *Request changes* on a content plan, here
 * *Request changes* on the deliverable) left live.
 *
 * **The answer stays on the row.** Before session 24 a row that had been answered
 * lost its pill and went back to looking exactly like the forty beside it, which is
 * the opposite of what the dialog promises ("it goes to the studio and stays on the
 * record"). And the studio's own note — the *question* — was never stored at all,
 * so the pill said "Awaiting your review" with nothing to say what to look at.
 * `reviewLineOf` prints whichever half is the client's business: the question while
 * it is open, their own answer once they have given one (R2-11).
 *
 * Those states used to be dead in the recorded world; `seed/world/casaturca.ts`
 * §reviews reaches all three of them now, so what is tested here is the real
 * payload. The held branch is still overridden onto the recording, because August is
 * released in full and holding one of its files is not a fact about this world.
 */

import type { ClientFilesPageView, FileEntryView } from '@fareast/shared'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import { useReviewFile } from '../../../api/client/index.js'
import { messageOf } from '../../../api/problem.js'
import { isImpersonating, usePrincipal } from '../../../auth/principal.js'
import { NoteDialog } from '../../../components/client/DecisionCard.js'
import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Fold, Empty } from '../../../components/portal/Disclosure.js'
import { FileRow } from '../../../components/portal/Files.js'
import { ScrollX, Stack } from '../../../components/portal/Layout.js'
import { Dialog } from '../../../components/portal/Overlay.js'
import { Notice, Pill } from '../../../components/portal/Status.js'
import { Table, TBody, Td, Th, THead, Tr } from '../../../components/portal/Table.js'
import {
  HeroCap,
  HeroFigure,
  More,
  Muted,
  Num,
  Prose,
  Small,
} from '../../../components/portal/Text.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import { abilitiesOf } from '../settings/roles.js'
import { DownloadLink } from './DownloadLink.js'
import { extensionOf, heldLabel } from './fileType.js'

/**
 * How many rows are on the card before the fold.
 *
 * Seven, because that is what the mockup draws. Anything a client has to act on
 * is promoted above the cut first, so the number is a budget for the routine
 * rather than a window on the newest.
 */
export const ROWS_ON_THE_CARD = 7

/**
 * The anchor on the earlier-months table.
 *
 * A month reads `Jul 2026` in two places on this page — here and in the reports
 * column — so the table an assertion (or a `#`-link from the report archive)
 * means has a name. `Common` allows an id and nothing else.
 */
export const EARLIER_MONTHS_ID = 'earlier-months'

/**
 * The sentence beside a disabled *Approve*, and the id the buttons point at.
 *
 * One sentence and one element for the whole card rather than one per row: the rule
 * is about the reader, not about the file, and a page of forty rows that repeats it
 * seven times is seven copies of the same fact. `aria-describedby` is what joins
 * them — `DecisionCard` makes the same choice for the same reason, and CONTRACT.md
 * §Common admits an id and ARIA and nothing else, so a `title` is not available and
 * would not be keyboard-reachable if it were.
 *
 * It is a plain `Notice` and **not** `Notice need`: DESIGN.md P4 gives ochre one
 * meaning — this needs your decision, or this costs you more — and a refusal
 * addressed to a studio member is neither. The page's one ochre thing stays the pill.
 */
export const REVIEW_IMPERSONATION_ID = 'review-impersonation'

export const REVIEW_IMPERSONATION_NOTE =
  'You are viewing this account as somebody else. Approving a deliverable is the client’s ' +
  'sign-off on the work and is theirs to give — ask them to answer it, or ask us to send it ' +
  'for review again. Requesting changes commits nothing and is still open to you.'

export type MonthFilesProps = {
  readonly month: ClientFilesPageView['month']
  readonly earlier: ClientFilesPageView['earlier']
}

export function MonthFiles({ month, earlier }: MonthFilesProps) {
  const format = useFormat()
  const principal = usePrincipal()
  const mayReview = abilitiesOf(principal).raisesRequests
  /*
   * What an impersonated session may not do here — the header's own paragraph.
   * `files.recordReview` refuses `approve` and admits `changes`, and this is that
   * one rule read rather than a second copy of it.
   */
  const approvalBlocked = isImpersonating(principal)
  const review = useReviewFile()

  /** The row whose *Request changes* dialog is open, and the last refusal. */
  const [changing, setChanging] = useState<FileEntryView | null>(null)
  /** The row whose *Approve* is waiting to be confirmed (R3-15). */
  const [approving, setApproving] = useState<FileEntryView | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const ordered = byAttentionFirst(month.entries)
  const shown = ordered.slice(0, ROWS_ON_THE_CARD)
  const folded = ordered.slice(ROWS_ON_THE_CARD)

  /*
   * `messageOf`, not `error.message`: `ApiProblem` extends `Error`, so the second
   * gives the generic title — "Conflict" — where `detail` is the sentence the
   * service wrote for this reader ("That review has already been answered. Ask us
   * to send it again if something changed."). `DecisionCard` makes the same choice
   * for the same reason.
   */
  const settled = {
    onSuccess: () => {
      setChanging(null)
      setApproving(null)
      setFailure(null)
    },
    onError: (error: Error) => {
      setFailure(messageOf(error))
    },
  }

  /*
   * A dialog is modal — Radix marks the rest of the page `aria-hidden` — so a
   * notice on the card behind an open one is unreachable to a reader and to a
   * screen reader. It is rendered in exactly one of the two places.
   */
  const failureNotice =
    failure === null ? null : (
      <Notice need role="alert">
        {failure}
      </Notice>
    )

  if (month.entries.length === 0) {
    return (
      <Card>
        <CardBody>
          <Empty>Nothing was delivered in {format.month(month.period)}.</Empty>
        </CardBody>
        {earlier.length === 0 ? null : (
          <CardBody>
            <EarlierMonths earlier={earlier} />
          </CardBody>
        )}
      </Card>
    )
  }

  return (
    <Card>
      <CardBody>
        <Stack gap="tight">
          <HeroCap>Files delivered in {format.month(month.period)}</HeroCap>
          <HeroFigure value={month.files} />
          <Prose>
            {month.all_released
              ? 'All of them released and downloadable, '
              : 'Some of them are still being held, '}
            <Num>{format.bytes(month.bytes)}</Num> in total.
            {/* One sentence, and the verb has to agree: the branch was dead in the
                recorded world, so "1 of them are waiting" had never been read. */}
            {month.awaiting_review === 0
              ? null
              : month.awaiting_review === 1
                ? ' One of them is waiting on your review.'
                : ` ${format.number(month.awaiting_review)} of them are waiting on your review.`}
          </Prose>
        </Stack>
      </CardBody>

      {approvalBlocked && mayReview && shown.some((entry) => entry.review === 'requested') ? (
        <CardBody>
          <Notice role="status" id={REVIEW_IMPERSONATION_ID}>
            {REVIEW_IMPERSONATION_NOTE}
          </Notice>
        </CardBody>
      ) : null}

      <CardBody tight>
        {shown.map((entry) => (
          <FileRow
            key={entry.id}
            icon={extensionOf(entry.name)}
            title={entry.title}
            sub={subOf(entry, format)}
            meta={
              <>
                <Num>{format.bytes(entry.size)}</Num>
                {entry.review === 'requested' ? (
                  <Pill state="need">Awaiting your review</Pill>
                ) : null}
                <Download entry={entry} />
                {entry.review === 'requested' && mayReview ? (
                  <>
                    {/*
                      `aria-disabled` beside `disabled`, and `aria-describedby`
                      pointing at the notice above — `DecisionCard`'s Approve does
                      exactly this, and its comment is the argument: a reader who
                      lands on the row is told the control is off rather than left
                      to infer it from the styling, and told why in a place they
                      can reach.
                    */}
                    <Button
                      size="sm"
                      disabled={approvalBlocked || review.isPending}
                      aria-disabled={approvalBlocked ? true : undefined}
                      aria-describedby={approvalBlocked ? REVIEW_IMPERSONATION_ID : undefined}
                      onClick={() => {
                        setFailure(null)
                        setApproving(entry)
                      }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setFailure(null)
                        setChanging(entry)
                      }}
                    >
                      Request changes
                    </Button>
                  </>
                ) : null}
              </>
            }
          />
        ))}
      </CardBody>

      {changing !== null || approving !== null ? null : failureNotice === null ? null : (
        <CardBody>{failureNotice}</CardBody>
      )}

      <CardBody>
        <Stack gap="tight">
          {folded.length === 0 ? null : (
            <Fold summary={`The other ${format.number(folded.length)} files this month`}>
              <ScrollX>
                <Table>
                  <THead>
                    <Tr>
                      <Th>Deliverable</Th>
                      <Th right>Size</Th>
                      <Th right>File</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {folded.map((entry) => (
                      <Tr key={entry.id}>
                        <Td>{entry.title}</Td>
                        <Td right num>
                          {format.bytes(entry.size)}
                        </Td>
                        <Td right>
                          <Download entry={entry} />
                        </Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </ScrollX>
            </Fold>
          )}
          {earlier.length === 0 ? null : <EarlierMonths earlier={earlier} />}
        </Stack>
      </CardBody>

      {/*
        R3-15. *Approve* wrote on the first click, with no dialog.

        **A confirmation, not an undo**, and the reason is which of the two questions
        the finding really asks. Can a client reach it by accident: yes, easily. It is
        a `size="sm"` button in the same `meta` strip as the *Download …* link and
        *Request changes*, three same-shaped controls a thumb-width apart on the rows
        `byAttentionFirst` has just promoted to the top of the page; a mis-click
        commits, the two controls vanish with the pill, and there is nothing left on
        the row that says what happened. Every other decision this portal offers a
        client confirms first — `DecisionCard` for a quote, an extra, a package change
        and a content plan — and *Request changes*, on this very row, already opens a
        dialog. Sign-off was the only one that did not.

        Reversible was considered and refused. `files.recordReview` answers `Conflict`
        on a second answer ("That review has already been answered"), which is
        `CLAUDE.md`'s "records are appended, never edited" and is right: the studio
        acts on a sign-off, and a client silently withdrawing one after we have moved
        is worse than a dialog. Taking it back is the studio's `files_request_review`
        — which is why the copy names it rather than pretending there is no way back.

        A plain `Dialog` and not `ConfirmDialog`: `Overlay.tsx` reserves that one for
        money, and this commits none. The button names the deliverable, because a page
        of forty rows is where the wrong one gets approved.
      */}
      <Dialog
        open={approving !== null}
        onOpenChange={(open) => {
          if (!open) setApproving(null)
        }}
        title={approving === null ? 'Approve' : `Approve ${approving.title}`}
      >
        <Stack gap="tight">
          <Small>
            <Muted>
              This is your sign-off and it goes to the studio on the record. You cannot take it back
              here — if something changes, ask us and we will send it for review again.
            </Muted>
          </Small>
          {failureNotice}
          <ButtonRow>
            <Button
              variant="primary"
              disabled={review.isPending}
              onClick={() => {
                if (approving === null) return
                review.mutate({ id: approving.id, decision: 'approve', note: '' }, settled)
              }}
            >
              {approving === null ? 'Approve it' : `Approve ${approving.title}`}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setApproving(null)
              }}
            >
              Not yet
            </Button>
          </ButtonRow>
        </Stack>
      </Dialog>

      <NoteDialog
        open={changing !== null}
        onOpenChange={(open) => {
          if (!open) setChanging(null)
        }}
        title={changing === null ? 'Request changes' : `Request changes — ${changing.title}`}
        submitLabel="Send to the studio"
        busy={review.isPending}
        onSubmit={(note) => {
          if (changing === null) return
          review.mutate({ id: changing.id, decision: 'changes', note }, settled)
        }}
        failure={failureNotice}
      >
        Nothing is approved and the file stays where it is. Say what should be different — it goes
        to the studio and stays on the record, and we send the next version back here.
      </NoteDialog>
    </Card>
  )
}

/* ------------------------------------------------------------------ the fold */

/**
 * Five months in a table, each with the link that opens it.
 *
 * The last column is the mockup's own, and it is not decoration: `PeriodSeg`
 * offers three months, so without a per-row link the two oldest rows in this fold
 * name a month with no way to reach it. It writes the same two search parameters
 * the seg does, because the URL is the state.
 */
function EarlierMonths({ earlier }: { readonly earlier: ClientFilesPageView['earlier'] }) {
  const format = useFormat()
  // `earlier` arrives newest first, so the range reads oldest-to-newest.
  const newest = earlier[0]?.period
  const oldest = earlier[earlier.length - 1]?.period
  const range =
    newest === undefined || oldest === undefined
      ? 'Earlier months'
      : `Earlier months — ${format.month(oldest)} to ${format.month(newest)}`

  return (
    <Fold summary={range}>
      <ScrollX>
        <Table id={EARLIER_MONTHS_ID}>
          <THead>
            <Tr>
              <Th>Month</Th>
              <Th>Deliverables</Th>
              <Th right>Files</Th>
              <Th right>Size</Th>
              <Th right>Open</Th>
            </Tr>
          </THead>
          <TBody>
            {earlier.map((row) => (
              <Tr key={row.period}>
                <Td date>{format.month(row.period)}</Td>
                <Td>
                  <Small>{deliverablesOf(row.items, format)}</Small>
                </Td>
                <Td right num>
                  {format.number(row.files)}
                </Td>
                <Td right num>
                  {format.bytes(row.bytes)}
                </Td>
                <Td right>
                  <More asChild aria-label={`Open ${format.month(row.period)}`}>
                    <Link to="/app/files" search={{ view: 'month', period: row.period }}>
                      Open
                    </Link>
                  </More>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </ScrollX>
    </Fold>
  )
}

/* --------------------------------------------------------------------- rows */

function Download({ entry }: { readonly entry: FileEntryView }) {
  if (!entry.released) return <Muted>{heldLabel(entry.kind)}</Muted>
  return <DownloadLink id={entry.id} label={`Download ${entry.title}`} />
}

/**
 * Anything the client has to act on, then everything else in the order the read
 * model sent — which is newest first.
 *
 * A `slice` down the middle of an unsorted list would hide a held file or a
 * review request behind a disclosure, and those are the two rows on the card
 * that are not routine.
 */
export function byAttentionFirst(entries: readonly FileEntryView[]): readonly FileEntryView[] {
  const needsYou = (entry: FileEntryView) => entry.review === 'requested' || !entry.released
  return [...entries.filter(needsYou), ...entries.filter((entry) => !needsYou(entry))]
}

/**
 * "Social content · delivered 5, 12, 19 Aug", when the read model knows either.
 *
 * `delivered_on` is a `DateOnly`, so it goes through `format.day` — a day read as
 * an instant is the 4th for anybody west of Greenwich, and `useFormat().day` is
 * the one place that conversion is written (`format/useFormat.ts`).
 *
 * The review sentence is the last clause when there is one, so the row that carries
 * the ochre pill also carries the words that go with it.
 */
function subOf(entry: FileEntryView, format: Formatter): string | undefined {
  const parts: string[] = []
  if (entry.services.length > 0) parts.push(entry.services.join(', '))
  if (entry.delivered_on.length > 0) {
    const days = entry.delivered_on.map((day) => format.day(day, 'day'))
    parts.push(`delivered ${days.join(', ')}`)
  }
  const review = reviewLineOf(entry, format)
  if (review !== undefined) parts.push(review)
  return parts.length === 0 ? undefined : parts.join(' · ')
}

/**
 * The review, in the client's own terms: the question while it is open, their own
 * answer once they have given one.
 *
 * Both halves were invisible before session 24 (R2-11), and each was invisible in its
 * own way. **The ask** was never stored — `files_request_review` put its note in the
 * mail and on the audit row and nowhere else — so the pill said "Awaiting your review"
 * over a fourteen-minute cut with no statement of which fourteen minutes. **The
 * answer** was stored and never read: the row lost its pill on submit and went back
 * to looking exactly like the forty beside it, which is the opposite of what the
 * dialog promises ("it goes to the studio and **stays on the record**").
 *
 * Once answered, the studio's question is dropped rather than printed beside the
 * reply. The client asked it and answered it; repeating it back is clutter on the
 * page they read every month. `pages/studio/clientDetail/FilesSection.tsx` prints
 * both, because for the studio the pair *is* the record.
 *
 * Nothing is truncated. A page states what it was given (DESIGN.md P2), and a client
 * who wrote three sentences is entitled to read all three of them back.
 */
function reviewLineOf(entry: FileEntryView, format: Formatter): string | undefined {
  if (entry.review === 'none') return undefined

  if (entry.review === 'requested') {
    const asked =
      entry.review_requested_at === null
        ? 'we asked for your review'
        : `we asked on ${format.date(entry.review_requested_at, 'short')}`
    return entry.review_request_note === '' ? asked : `${asked} — ${entry.review_request_note}`
  }

  const when = entry.reviewed_at === null ? '' : ` on ${format.date(entry.reviewed_at, 'short')}`
  const answer =
    entry.review === 'approved' ? `you approved this${when}` : `you asked for changes${when}`
  return entry.review_note === '' ? answer : `${answer} — “${entry.review_note}”`
}

/**
 * "20 reel · 5 seo_content · 10 video_editing".
 *
 * The rate-card **code** is what `earlier[].items` publishes; the mockup prints
 * "12 reels, 7 carousels, 5 statics" and there is no noun on the wire to build
 * that from. The content type is a word when the read model has one and the
 * service code is what stands in when it does not.
 */
function deliverablesOf(
  items: ClientFilesPageView['earlier'][number]['items'],
  format: Formatter,
): string {
  return items
    .map((item) => `${format.number(item.qty)} ${item.content_type ?? item.service}`)
    .join(' · ')
}
